import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut,
  ArrowLeft,
  Users as UsersIcon,
  Calendar,
  MessageSquare,
  Tag,
  TrendingUp,
  Edit2,
  Check,
  X,
  RefreshCw,
  Settings as SettingsIcon,
  Flame,
  Sparkles,
  AlertTriangle,
  Save,
  Eye,
  EyeOff,
} from 'lucide-react';
import api, { formatApiErrorDetail } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const TABS = [
  { key: 'overview', label: 'Overview', icon: TrendingUp },
  { key: 'bookings', label: 'Bookings', icon: Calendar },
  { key: 'contacts', label: 'Inquiries', icon: MessageSquare },
  { key: 'users', label: 'Users', icon: UsersIcon },
  { key: 'services', label: 'Services', icon: Tag },
  { key: 'settings', label: 'Settings', icon: SettingsIcon },
];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [detailBookingId, setDetailBookingId] = useState(null);

  const onLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="dashboard" data-testid="admin-page">
      <header className="dashboard-header">
        <div className="dashboard-header-inner">
          <Link to="/" className="dashboard-logo">SOUL ALIGNED</Link>
          <div className="dashboard-user">
            <span className="dashboard-user-name">
              {user?.name}
              <span className="badge-admin">Admin</span>
            </span>
            <Link to="/dashboard" className="btn-ghost" data-testid="back-to-user-dashboard">
              <ArrowLeft size={14} /> User view
            </Link>
            <button onClick={onLogout} className="btn-ghost" data-testid="logout-btn">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="admin-shell">
        <nav className="admin-tabs" role="tablist" data-testid="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`admin-tab ${tab === t.key ? 'active' : ''}`}
                data-testid={`admin-tab-${t.key}`}
                role="tab"
                aria-selected={tab === t.key}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </nav>

        <main className="admin-content">
          {tab === 'overview' && <Overview />}
          {tab === 'bookings' && <BookingsTab onOpenDetail={setDetailBookingId} />}
          {tab === 'contacts' && <ContactsTab />}
          {tab === 'users' && <UsersTab />}
          {tab === 'services' && <ServicesTab />}
          {tab === 'settings' && <SettingsTab />}
        </main>
      </div>

      {detailBookingId && (
        <BookingDetailModal bookingId={detailBookingId} onClose={() => setDetailBookingId(null)} />
      )}
    </div>
  );
}

// ---------------------------- Overview ----------------------------
function Overview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/analytics');
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="dash-loading">Loading analytics…</div>;
  if (!data) return <div>Failed to load.</div>;

  const cards = [
    { label: 'Active users', value: data.users, color: 'purple' },
    { label: 'Total bookings', value: data.bookings_total, color: 'blue' },
    { label: 'Paid bookings', value: data.bookings_paid, color: 'green' },
    { label: 'Pending payments', value: data.bookings_pending, color: 'orange' },
    { label: 'Discovery calls', value: data.discovery_calls, color: 'pink' },
    { label: 'Contact inquiries', value: data.contacts, color: 'blue' },
  ];

  return (
    <div data-testid="admin-overview">
      <div className="admin-section-head">
        <h2>At a glance</h2>
        <button onClick={load} className="btn-ghost" data-testid="refresh-analytics"><RefreshCw size={14} /> Refresh</button>
      </div>
      <div className="admin-stats-grid">
        {cards.map((c) => (
          <div key={c.label} className={`admin-stat-card stat-${c.color}`} data-testid={`stat-${c.label}`}>
            <div className="admin-stat-value">{c.value}</div>
            <div className="admin-stat-label">{c.label}</div>
          </div>
        ))}
        <div className="admin-stat-card stat-dark stat-revenue" data-testid="stat-revenue">
          <div className="admin-stat-value">₹{(data.revenue_inr || 0).toLocaleString('en-IN')}</div>
          <div className="admin-stat-label">Revenue (paid only)</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------- Bookings ----------------------------
function BookingsTab({ onOpenDetail }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'paid') params.payment_status = 'paid';
      else if (filter === 'pending') params.payment_status = 'pending';
      const { data } = await api.get('/admin/bookings', { params });
      const filtered = filter === 'discovery' ? data.filter((b) => b.is_discovery_call) : data;
      setItems(filtered);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div data-testid="admin-bookings">
      <div className="admin-section-head">
        <h2>Bookings</h2>
        <div className="admin-filters">
          {['all', 'paid', 'pending', 'discovery'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`chip ${filter === f ? 'chip-active' : ''}`}
              data-testid={`bookings-filter-${f}`}
            >{f}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="dash-loading">Loading…</div>
        : items.length === 0 ? <div className="dash-empty">No bookings match this filter.</div>
        : (
        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="bookings-table">
            <thead><tr><th>Service</th><th>User</th><th>For</th><th>Amount</th><th>Status</th><th>Payment</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.booking_id} data-testid={`booking-row-${b.booking_id}`}>
                  <td>
                    <div className="cell-strong">{b.service_title}</div>
                    {b.is_discovery_call && <span className="chip chip-mini chip-pink">Discovery</span>}
                    {b.ai_analysis && <span className="chip chip-mini chip-purple"><Sparkles size={10}/> AI ready</span>}
                  </td>
                  <td>
                    <div>{b.user_name}</div>
                    <div className="cell-muted">{b.user_email}</div>
                  </td>
                  <td>
                    {b.booking_for === 'other'
                      ? <span className="chip chip-mini">For: {b.recipient_name || 'other'}</span>
                      : <span className="cell-muted">self</span>}
                  </td>
                  <td>{b.is_free ? '—' : `₹${b.amount.toLocaleString('en-IN')}`}</td>
                  <td><span className={`status status-${b.status}`}>{b.status}</span></td>
                  <td><span className={`status status-${b.payment_status}`}>{b.payment_status}</span></td>
                  <td className="cell-muted">{new Date(b.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                  <td>
                    <button onClick={() => onOpenDetail(b.booking_id)} className="btn-ghost" data-testid={`open-booking-${b.booking_id}`}>
                      <Eye size={13}/> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------- Booking detail modal w/ AI ----------------------------
function BookingDetailModal({ bookingId, onClose }) {
  const [booking, setBooking] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings/${bookingId}`);
      setBooking(data);
      setNotes(data.admin_notes || '');
      try {
        const { data: q } = await api.get(`/services/${data.service_id}/questionnaire`);
        setQuestionnaire(q);
      } catch (qErr) {
        // Questionnaire is optional — log but don't block booking detail render
        console.warn('questionnaire fetch failed', qErr);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || 'Could not load booking');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [bookingId]);

  const analyze = async () => {
    setAnalyzing(true);
    setError('');
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/ai-analyze`);
      setBooking((b) => ({ ...b, ai_analysis: data }));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || 'AI analysis failed');
    } finally { setAnalyzing(false); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/admin/bookings/${bookingId}/notes`, { admin_notes: notes });
      setBooking((b) => ({ ...b, admin_notes: notes }));
    } finally { setSavingNotes(false); }
  };

  const q_map = Object.fromEntries((questionnaire?.questions || []).map((q) => [q.key, q]));
  const ai = booking?.ai_analysis;

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="booking-detail-modal">
      <div className="modal-card modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Booking detail</h3>
          <button onClick={onClose} className="btn-ghost" data-testid="close-booking-detail"><X size={16} /></button>
        </div>

        {loading ? <div className="dash-loading">Loading…</div> : booking && (
          <div className="booking-detail">
            <div className="booking-detail-head">
              <div>
                <div className="cell-strong" style={{ fontSize: '1.1rem' }}>{booking.service_title}</div>
                <div className="cell-muted">{booking.user_name} · {booking.user_email} · {booking.user_mobile || 'no mobile'}</div>
                <div className="cell-muted">
                  Booked {new Date(booking.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </div>
              <div className="booking-detail-pills">
                <span className={`status status-${booking.status}`}>{booking.status}</span>
                <span className={`status status-${booking.payment_status}`}>{booking.payment_status}</span>
                <span className="chip chip-mini">{booking.is_free ? 'Free' : `₹${booking.amount.toLocaleString('en-IN')}`}</span>
                {booking.booking_for === 'other' && (
                  <span className="chip chip-mini chip-purple">
                    For: {booking.recipient_name || 'other'}
                  </span>
                )}
              </div>
            </div>

            {/* Questionnaire answers */}
            <div className="detail-section">
              <h4>Questionnaire</h4>
              {Object.keys(booking.answers || {}).length === 0 ? (
                <p className="cell-muted">No questionnaire answers.</p>
              ) : (
                <dl className="qa-list">
                  {Object.entries(booking.answers).map(([key, val]) => (
                    <div key={key} className="qa-row">
                      <dt>{q_map[key]?.label || key.replace(/_/g, ' ')}</dt>
                      <dd>{String(val) || <span className="cell-muted">(blank)</span>}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {booking.notes && (
                <div className="qa-row" style={{ marginTop: '0.75rem' }}>
                  <dt>User notes</dt>
                  <dd>{booking.notes}</dd>
                </div>
              )}
            </div>

            {/* AI analysis */}
            <div className="detail-section">
              <div className="detail-section-head">
                <h4><Sparkles size={14} /> AI clinical brief</h4>
                <button
                  onClick={analyze}
                  disabled={analyzing}
                  className="btn-primary btn-mini"
                  data-testid="run-ai-analysis"
                >
                  {analyzing ? 'Analyzing…' : ai ? 'Regenerate' : 'Generate brief'}
                </button>
              </div>

              {!ai && !analyzing && (
                <p className="cell-muted">
                  Click <strong>Generate brief</strong> to have Claude analyze the questionnaire and surface
                  possible concerns, key symptoms, suggested modalities, and safe next steps.
                </p>
              )}

              {ai && (
                <div className="ai-brief" data-testid="ai-brief">
                  {ai.safety_flag && (
                    <div className="safety-alert" data-testid="safety-alert">
                      <AlertTriangle size={16} /> <strong>Safety flag</strong> — review immediately.
                    </div>
                  )}
                  {ai.summary && <p className="ai-summary">{ai.summary}</p>}

                  <AISection title="Possible concerns" items={ai.possible_concerns} />
                  {ai.emotional_state && (
                    <div className="ai-row">
                      <span className="ai-label">Emotional state</span>
                      <span className="ai-text">{ai.emotional_state}</span>
                    </div>
                  )}
                  <AISection title="Key symptoms" items={ai.key_symptoms} />
                  <AISection title="Suggested modalities" items={ai.suggested_modalities} accent />
                  <AISection title="Recommended next steps" items={ai.recommended_next_steps} />

                  {ai.generated_at && (
                    <div className="cell-muted ai-foot">
                      Generated {new Date(ai.generated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      {ai.error && ' · ' + ai.error}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Admin notes */}
            <div className="detail-section">
              <h4>Admin / Healer notes</h4>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Add private notes about this client / session — only visible to you"
                data-testid="admin-notes-textarea"
              />
              <button
                onClick={saveNotes}
                disabled={savingNotes}
                className="btn-primary btn-mini"
                data-testid="save-admin-notes"
              >
                <Save size={12} /> {savingNotes ? 'Saving…' : 'Save notes'}
              </button>
              {booking.admin_notes && booking.admin_notes !== notes && (
                <span className="cell-muted" style={{ marginLeft: '0.5rem' }}>(unsaved changes)</span>
              )}
            </div>

            {error && <div className="auth-error" data-testid="detail-error">{error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function AISection({ title, items, accent }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="ai-row">
      <span className="ai-label">{title}</span>
      <div className={`ai-chips ${accent ? 'ai-chips-accent' : ''}`}>
        {items.map((it, i) => <span key={`${it}-${i}`} className="ai-chip">{it}</span>)}
      </div>
    </div>
  );
}

// ---------------------------- Contacts ----------------------------
function ContactsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get('/admin/contacts'); setItems(data); }
    finally { setLoading(false); }
  })(); }, []);
  return (
    <div data-testid="admin-contacts">
      <div className="admin-section-head"><h2>Contact inquiries</h2></div>
      {loading ? <div className="dash-loading">Loading…</div>
        : items.length === 0 ? <div className="dash-empty">No inquiries yet.</div>
        : (
        <div className="admin-cards-grid">
          {items.map((c) => (
            <div key={c.contact_id} className="admin-mini-card" data-testid={`contact-${c.contact_id}`}>
              <div className="admin-mini-head">
                <strong>{c.name}</strong>
                <span className="cell-muted">{new Date(c.created_at).toLocaleString('en-IN', { dateStyle: 'medium' })}</span>
              </div>
              <a href={`mailto:${c.email}`} className="cell-link">{c.email}</a>
              {c.phone && <div className="cell-muted">{c.phone}</div>}
              {c.service && <span className="chip chip-mini">{c.service}</span>}
              {c.message && <p className="admin-msg">{c.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------- Users tab w/ warm-lead toggle ----------------------------
function UsersTab() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get('/admin/users'), api.get('/services')]);
      setItems(u.data);
      setServices(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleWarm = async (u) => {
    const next = !u.is_warm_lead;
    setItems((items) => items.map((x) => x.user_id === u.user_id ? { ...x, is_warm_lead: next } : x));
    try {
      await api.put(`/admin/users/${u.user_id}/warm-lead`, { is_warm_lead: next });
    } catch {
      // revert on failure
      setItems((items) => items.map((x) => x.user_id === u.user_id ? { ...x, is_warm_lead: !next } : x));
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="admin-section-head"><h2>Users</h2></div>
      {loading ? <div className="dash-loading">Loading…</div> : (
        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="users-table">
            <thead><tr><th>Name</th><th>Email</th><th>Mobile</th><th>Role</th><th>Warm lead</th><th>Curated</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td className="cell-muted">{u.mobile || '—'}</td>
                  <td><span className={`status status-${u.role}`}>{u.role}</span></td>
                  <td>
                    {u.role === 'admin' ? <span className="cell-muted">—</span> : (
                      <button
                        onClick={() => toggleWarm(u)}
                        className={`warm-toggle ${u.is_warm_lead ? 'on' : ''}`}
                        data-testid={`warm-toggle-${u.user_id}`}
                        aria-pressed={!!u.is_warm_lead}
                      >
                        <Flame size={13} /> {u.is_warm_lead ? 'Warm' : 'Cold'}
                      </button>
                    )}
                  </td>
                  <td className="cell-muted">
                    {(u.curated_services || []).length ? (u.curated_services || []).join(', ') : '—'}
                  </td>
                  <td>
                    {u.role !== 'admin' && (
                      <button onClick={() => setEditing(u)} className="btn-ghost" data-testid={`curate-btn-${u.user_id}`}>
                        <Edit2 size={13} /> Curate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <CurateModal
          user={editing}
          services={services}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CurateModal({ user, services, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set(user.curated_services || []));
  const [saving, setSaving] = useState(false);
  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.user_id}/curated-services`, { service_ids: Array.from(selected) });
      onSaved();
    } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose} data-testid="curate-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Curate services for {user.name}</h3>
          <button onClick={onClose} className="btn-ghost"><X size={16} /></button>
        </div>
        <p className="cell-muted">Pick the services you specifically recommend to this user.</p>
        <div className="curate-list">
          {services.map((s) => (
            <label key={s.service_id} className="curate-item">
              <input
                type="checkbox"
                checked={selected.has(s.service_id)}
                onChange={() => toggle(s.service_id)}
                data-testid={`curate-check-${s.service_id}`}
              />
              <div>
                <div className="cell-strong">{s.title}</div>
                <div className="cell-muted">{s.is_free ? 'Free' : `₹${s.price.toLocaleString('en-IN')}`}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="modal-actions">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} className="btn-primary" disabled={saving} data-testid="curate-save">
            {saving ? 'Saving…' : 'Save'} <Check size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------- Services tab ----------------------------
function ServicesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [saveErr, setSaveErr] = useState('');

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/services'); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (s) => {
    setEditing(s.service_id);
    setDraft({ price: s.price, badge: s.badge || '', duration: s.duration });
    setSaveErr('');
  };

  const save = async (id) => {
    setSaveErr('');
    try {
      await api.put(`/admin/services/${id}`, {
        price: Number(draft.price),
        badge: draft.badge === '' ? null : draft.badge,
        duration: draft.duration,
      });
      setEditing(null);
      load();
    } catch (e) {
      setSaveErr(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-services">
      <div className="admin-section-head"><h2>Services & pricing</h2></div>
      {loading ? <div className="dash-loading">Loading…</div> : (
        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="services-table">
            <thead><tr><th>Service</th><th>Duration</th><th>Price</th><th>Badge</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.service_id} data-testid={`service-row-${s.service_id}`}>
                  <td>
                    <div className="cell-strong">{s.title}</div>
                    <div className="cell-muted">{s.service_id}</div>
                  </td>
                  <td>
                    {editing === s.service_id
                      ? <input type="text" value={draft.duration || ''} onChange={(e) => setDraft({ ...draft, duration: e.target.value })} data-testid={`edit-duration-${s.service_id}`} />
                      : <span className="cell-muted">{s.duration}</span>}
                  </td>
                  <td>
                    {editing === s.service_id
                      ? <input type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} data-testid={`edit-price-${s.service_id}`} disabled={s.is_free} />
                      : s.is_free ? '—' : `₹${s.price.toLocaleString('en-IN')}`}
                  </td>
                  <td>
                    {editing === s.service_id
                      ? <select value={draft.badge} onChange={(e) => setDraft({ ...draft, badge: e.target.value })} data-testid={`edit-badge-${s.service_id}`}>
                          <option value="">None</option>
                          <option value="Most Popular">Most Popular</option>
                          <option value="Recommended">Recommended</option>
                        </select>
                      : s.badge || '—'}
                  </td>
                  <td>
                    {editing === s.service_id
                      ? <>
                          <button onClick={() => save(s.service_id)} className="btn-primary btn-mini" data-testid={`save-service-${s.service_id}`}>Save</button>
                          <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
                        </>
                      : <button onClick={() => startEdit(s)} className="btn-ghost" data-testid={`edit-service-${s.service_id}`}><Edit2 size={13} /> Edit</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {saveErr && <div className="auth-error">{saveErr}</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------------- Settings (Razorpay) ----------------------------
function SettingsTab() {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings/razorpay');
      setCurrent(data);
      setKeyId(data.key_id);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      const { data } = await api.put('/admin/settings/razorpay', { key_id: keyId.trim(), key_secret: keySecret.trim() });
      setCurrent(data);
      setKeySecret('');
      setMsg('Razorpay credentials updated. The next paid booking will use these keys.');
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-settings">
      <div className="admin-section-head"><h2>Settings</h2></div>

      <div className="settings-card">
        <div className="settings-card-head">
          <h3>Razorpay payment gateway</h3>
          {current?.is_configured && (
            <span className="chip chip-mini chip-green">
              <Check size={10} /> Configured · source: {current.source}
            </span>
          )}
        </div>
        <p className="cell-muted">
          Used to charge for paid bookings. Get your keys from
          <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer"> Razorpay Dashboard → Account & Settings → API Keys</a>.
          Use <strong>test</strong> keys (rzp_test_…) during testing and <strong>live</strong> keys for real charges.
        </p>

        {loading ? <div className="dash-loading">Loading…</div> : (
          <form onSubmit={save} className="settings-form">
            <div className="auth-field">
              <label>Key ID</label>
              <input
                type="text"
                value={keyId}
                onChange={(e) => setKeyId(e.target.value)}
                placeholder="rzp_test_xxxxx or rzp_live_xxxxx"
                required
                data-testid="razorpay-key-id"
              />
            </div>
            <div className="auth-field">
              <label>Key Secret {current?.is_configured && <span className="cell-muted">(stored: {current.key_secret_masked})</span>}</label>
              <div className="auth-input-wrap">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={keySecret}
                  onChange={(e) => setKeySecret(e.target.value)}
                  placeholder={current?.is_configured ? 'Leave blank to keep current; type a new secret to replace' : 'Paste the secret here'}
                  required={!current?.is_configured}
                  minLength={8}
                  data-testid="razorpay-key-secret"
                />
                <button type="button" onClick={() => setShowSecret(!showSecret)} className="btn-ghost" tabIndex={-1}>
                  {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {msg && <div className="settings-success" data-testid="settings-success">{msg}</div>}
            {err && <div className="auth-error" data-testid="settings-error">{err}</div>}

            <button type="submit" disabled={saving} className="btn-primary" data-testid="save-razorpay">
              <Save size={14} /> {saving ? 'Saving…' : 'Save Razorpay keys'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
